import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DatePipe, formatDate } from '@angular/common';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  CustomerReportModel,
  DownloadVendorReportModel,
  FilterServiceRequestModel,
  IBlockTimeReportInvoiceIdViewModel,
  LegReportViewModel,
  ReportFilterViewModel,
  ServiceRequestViewModel,
  SrReportViewModel,
  VendorCsvReportModel,
  VendorReportModel,
} from '../model/report.model';
import { filterServiceRequestData, filterInvoiceStatusReport } from './filterData.model';
import {
  IBlockTimeItem,
  IBlockTimeRequestResponse,
  ISplitBlockTimeRecordPayload,
  ISplittedBlockTimeRecordViewModel,
} from '../ReusableComponent/block-time-report/block-time-report.model';
import { ExportService } from './exportCSV.service';
import { IAirportCodeList } from './vehicle.model';
import { IServerResponseInvoiceStatusResponseModel } from '../ReusableComponent/invoice-status-report/invoice-status-report.model';
import { environment } from '@environments/environment';
import { DATE_TIME_FORMATE } from '@properties/date-time.properties';
import { TimezoneSharedService } from '@services/timezone/timezone-shared.service';
import { TripBlockInfoInterfaces } from '../ReusableComponent/block-time-report/interfaces/trip-block-info.interfaces';
import { AircraftInterface } from '../ReusableComponent/block-time-report/interfaces/aircraft.interface';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  // todo need to refactor to remove filterBlockTimeReportData, openAddBlockTimeRecordPopUp, loaderState, showCountForBlockTimeReport
  public filterBlockTimeReportData = new Subject<boolean>();
  public openAddBlockTimeRecordPopUp = new Subject();
  public loaderState = new BehaviorSubject<boolean>(false);
  public showCountForBlockTimeReport = new Subject();

  constructor(
    private httpClient: HttpClient,
    private exportService: ExportService,
    private datePipe: DatePipe,
    private readonly timezoneSharedService: TimezoneSharedService,
  ) {}

  getCustomerDetailReport(reportFilter: ReportFilterViewModel): Observable<CustomerReportModel> {
    const url = `${environment.apiUrl}/Report/FetchCustomerDetail`;

    return this.httpClient
      .post<CustomerReportModel>(url, reportFilter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getVendorDetailReport(reportFilter: ReportFilterViewModel): Observable<VendorReportModel> {
    const url = `${environment.apiUrl}/Report/FetchVendorDetail`;

    return this.httpClient
      .post<VendorReportModel>(url, reportFilter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredServiceRequestData(filter: filterServiceRequestData, offsetValue, isCustomerSRQueuePage): Observable<ServiceRequestViewModel> {
    const url = `${environment.apiUrl}/ServiceRequest/FilterServiceRequest/${offsetValue}/${isCustomerSRQueuePage}`;

    return this.httpClient
      .post<ServiceRequestViewModel>(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchVendorCsvData(vendorData: DownloadVendorReportModel): Observable<VendorCsvReportModel> {
    const url = `${environment.apiUrl}/Report/FetchVendorCsvData`;

    return this.httpClient
      .post<VendorCsvReportModel>(url, vendorData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchServiceRequestCsvData(FilterServiceRequest: FilterServiceRequestModel): Observable<SrReportViewModel[]> {
    const offsetValue = this.timezoneSharedService.offsetMinutes;
    const url = `${environment.apiUrl}/Report/FetchServiceRequestCsvData/${offsetValue}`;

    return this.httpClient
      .post<SrReportViewModel[]>(url, FilterServiceRequest)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchServiceRequestStepCsvData(ServiceRequestIdList: LegReportViewModel): Observable<VendorCsvReportModel[]> {
    const url = `${environment.apiUrl}/Report/FetchServiceRequestStepCsvData`;

    return this.httpClient
      .post<VendorCsvReportModel[]>(url, ServiceRequestIdList)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addBlockTimeRecord(record: IBlockTimeItem, isEdit: boolean): Observable<string> {
    const url = `${environment.apiUrl}/Report/AddEditBlockTimeRecord/${isEdit}`;

    return this.httpClient.post<string>(url, record).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteBlockTimeRecord(blockTimeRecordId: string): Observable<boolean> {
    const url = `${environment.apiUrl}/Report/DeleteBlockTimeRecord/${blockTimeRecordId}`;

    return this.httpClient.delete<boolean>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchBlockTimeReport(reportFilter: filterServiceRequestData): Observable<IBlockTimeRequestResponse> {
    const url = `${environment.apiUrl}/Report/fetchBlockTimeReport`;

    return this.httpClient
      .post<IBlockTimeRequestResponse>(url, reportFilter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getBlockTimeRecordToSplit(tripId: string, tailNumber: string): Observable<ISplittedBlockTimeRecordViewModel[]> {
    const url = `${environment.apiUrl}/Report/GetBlockTimeRecordToSplit/${tripId}/${tailNumber}`;

    return this.httpClient
      .get<ISplittedBlockTimeRecordViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  splitBlockTimeRecord(payload: ISplitBlockTimeRecordPayload): Observable<boolean> {
    const url = `${environment.apiUrl}/Report/SplitBlockTimeRecord`;

    return this.httpClient.post<boolean>(url, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getBlockTimeReportInvoiceIdList(): Observable<IBlockTimeReportInvoiceIdViewModel[]> {
    const url = `${environment.apiUrl}/Report/GetBlockTimeReportInvoiceIdList`;

    return this.httpClient
      .get<IBlockTimeReportInvoiceIdViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateArOrAP(record: IBlockTimeItem): Observable<boolean> {
    const url = `${environment.apiUrl}/Report/UpdateArOrAp`;

    return this.httpClient.put<boolean>(url, record).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchInvoiceStatusReport(reportFilter: filterInvoiceStatusReport): Observable<IServerResponseInvoiceStatusResponseModel> {
    const url = `${environment.apiHost}/NetSuite/getInvoiceStatusReport`;

    return this.httpClient
      .post<IServerResponseInvoiceStatusResponseModel>(url, reportFilter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo this function should be on some separate helper
  calculateMin(a, b) {
    const date1 = new Date(a);
    const date2 = new Date(b);
    const diffTime = Math.abs(date2.getTime() - date1.getTime());

    return diffTime / (1000 * 60);
  }

  // todo this function should be on some separate helper
  hourMinConversion(min) {
    return (
      (Math.floor(min / 60) < 10 ? '0' + Math.floor(min / 60) : Math.floor(min / 60)) +
      ' h ' +
      (Number((min % 60).toFixed(0)) < 10 ? '0' + (min % 60).toFixed(0) : (min % 60).toFixed(0)) +
      ' m'
    );
  }

  // todo this function should be on some separate helper
  convertToMST(time): string | null {
    if (time) {
      const utcTime = this.convertToUTC(time);
      return formatDate(utcTime, DATE_TIME_FORMATE, 'en-US', 'MST');
    }
    return null;
  }

  // todo this function should be on some separate helper
  convertToUTC(time): string | null {
    if (time) {
      const formattedTime = this.datePipe.transform(time, DATE_TIME_FORMATE);
      const convertedTime = new Date(formattedTime + ' UTC');
      return this.datePipe.transform(convertedTime, DATE_TIME_FORMATE);
    }
    return null;
  }

  // todo this function should be on some separate helper
  generateSrReport(serviceRequestList: any, reportName: string) {
    let srData: any = [];
    let legData: any = [];
    serviceRequestList.forEach((serviceRequest) => {
      let b = '';
      let estimatedSourceTime = 0;
      let actualSourceTime = 0;
      let estimatedDestinationTime = 0;
      let actualDestinationTime = 0;
      if (serviceRequest.legDetails != null) {
        const outBoundLeg = serviceRequest.legDetails.filter((x) => x.direction === 'Outbound');
        const inBoundLeg = serviceRequest.legDetails.filter((x) => x.direction === 'Inbound');
        outBoundLeg.forEach((item) => {
          estimatedSourceTime = estimatedSourceTime + this.calculateMin(item.plannedStartTime, item.plannedEndTime);
        });
        outBoundLeg.forEach((item) => {
          actualSourceTime = actualSourceTime + this.calculateMin(item.actualStartTime, item.actualEndTime);
        });
        inBoundLeg.forEach((item) => {
          estimatedDestinationTime = estimatedDestinationTime + this.calculateMin(item.plannedStartTime, item.plannedEndTime);
        });
        inBoundLeg.forEach((item) => {
          actualDestinationTime = actualDestinationTime + this.calculateMin(item.actualStartTime, item.actualEndTime);
        });
      }
      srData.push({
        TripID: serviceRequest.tripID,
        Customer: serviceRequest.customerName,
        'Service Request Type': serviceRequest.serviceType,
        'Organ Type': serviceRequest.serviceType === 'Organ' ? serviceRequest.serviceSubType : '-',
        Origin: serviceRequest.source + ' ' + serviceRequest.sourceAddress + ' ' + serviceRequest.sourceAddressLocation,
        Destination: serviceRequest.destination.map((element) => {
          b = b + element.destination + ' ' + element.destinationAddress + ' ' + element.destinationAddressLocation + ',';
          return b;
        })[serviceRequest.destination.length - 1],
        'Inbound D2D(Actual)': actualDestinationTime === 0 ? '00 h 00 m' : this.hourMinConversion(actualDestinationTime),
        'Outbound D2D(Actual)': actualSourceTime === 0 ? '00 h 00 m' : this.hourMinConversion(actualSourceTime),
        'Inbound D2D(Planned)': estimatedDestinationTime === 0 ? '00 h 00 m' : this.hourMinConversion(estimatedDestinationTime),
        'Outbound D2D(Planned)': estimatedSourceTime === 0 ? '00 h 00 m' : this.hourMinConversion(estimatedSourceTime),
        'Creation On (MST)': this.convertToMST(serviceRequest.createdOn),
        'Created By': serviceRequest.createdByMemberName,
        'UNOS ID': serviceRequest.unos === '' ? '-' : serviceRequest.unos,
      });
      serviceRequest.legDetails.forEach((x, index) => {
        legData.push({
          'Trip Id': serviceRequest.tripID,
          'Customer Name': serviceRequest.customerName,
          LegNumber: index + 1,
          Origin: x.origin + ' ' + x.originAddress + ' ' + x.originAddressLocation,
          Destination: x.destination + ' ' + x.destinationAddress + ' ' + x.destinationAddressLocation,
          'Pickup Time(Scheduled)': this.convertToMST(x.plannedStartTime),
          'Drop-off Time (Scheduled)': this.convertToMST(x.plannedEndTime),
          'Pickup Time(Actual)': this.convertToMST(x.actualStartTime),
          'Drop-off Time(Actual)': this.convertToMST(x.actualEndTime),
          'On-Scene Time (Actual)':
            x.transportType === 'Air'
              ? '-'
              : x.groudLegTimeStamp.onsceneTime != null
                ? this.convertToMST(x.groudLegTimeStamp.onsceneTime)
                : '-',
          'Transport Type': x.transportType,
          Direction: x.direction,
          'Provider Name': x.provider.memberName == null ? '-' : x.provider.providerName == null ? 'Internal' : x.provider.providerName,
          'Driver Name': x.provider.memberName == null ? '-' : x.provider.memberName,
          'Driver Contact': x.provider.memberName == null ? '-' : x.provider.providerContact,
          'Vehicle Name': x.model == null ? '-' : x.model,
          'Vehicle Number': x.tailNumber == null ? '-' : x.tailNumber,
        });
      });
    });
    this.exportService.exportAsExcelFile(srData, reportName, legData);
  }

  // todo this function should be on some separate helper
  getAirportCode(airportCode: string, airportCodeList: IAirportCodeList[]) {
    const airportAddress: IAirportCodeList = airportCodeList.find((record) => record.airportCode === airportCode);
    return airportAddress ? [airportAddress] : [];
  }

  getTripBlockInfo(tripId: string): Observable<TripBlockInfoInterfaces> {
    const url = `${environment.apiUrl}/Report/GetTripBlockInfo/${tripId}`;

    return this.httpClient.get<TripBlockInfoInterfaces>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAircraftsForVendors(vendorId: string): Observable<AircraftInterface[]> {
    const url = `${environment.apiUrl}/Report/GetAircraftsForVendor/${vendorId}`;

    return this.httpClient.get<AircraftInterface[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
